import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { Popover, PopoverTrigger, PopoverContent as PopoverContentBase } from '../components/ui/popover';
import { Checkbox as CheckboxBase } from '../components/ui/checkbox';
import { Building2, ChevronDown, X, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip,
  ResponsiveContainer, CartesianGrid, LabelList, Cell
} from 'recharts';

const BLUE = '#1d4ed8';
const SKY = '#0ea5e9';

interface HubSpotCRMProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface Summary {
  connected: boolean;
  total_open_value: number;
  open_deals_count: number;
  won_deals_count: number;
  lost_deals_count: number;
  win_rate: number;
  total_contacts: number;
  total_companies: number;
  activity_to_deal_index: number | null;
  stage_breakdown: { name: string; value: number; fill: string }[];
  value_by_owner: { name: string; value: number; deals: number }[];
  all_stages: string[];
  all_owners: string[];
  all_domains: string[];
}
interface CompanyRow { id: string; name: string; tier: string; numberofemployees: number; sample_contact: string | null; contact_count: number; }

const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const PopoverContent: any = PopoverContentBase;
const Checkbox: any = CheckboxBase;

function MultiSelect({ label, options, selected, onChange }: { label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void; }) {
  const toggle = (opt: string) => {
    onChange(selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt]);
  };
  if (options.length === 0) {
    return (
      <button
        data-testid={`hubspot-filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
        disabled
        title={`No ${label.toLowerCase()} data available on the connected HubSpot records yet.`}
        className="px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium border bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed"
      >
        <span>{label} (unavailable)</span>
      </button>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`hubspot-filter-${label.toLowerCase().replace(/\s+/g, '-')}`} className="px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium border bg-white text-slate-700 border-slate-200 hover:border-slate-300 transition-colors">
          <span>{label}{selected.length > 0 ? ` (${selected.length})` : ''}</span>
          <ChevronDown className="h-3 w-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 bg-white max-h-72 overflow-y-auto" align="start">
        {options.length === 0 ? (
          <p className="text-xs text-slate-400 p-2">No options</p>
        ) : options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-xs text-slate-700">
            <Checkbox checked={selected.includes(opt)} onCheckedChange={() => toggle(opt)} />
            <span className="truncate">{opt}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function HubSpotCRM({ onHover }: HubSpotCRMProps) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [contactVelocity, setContactVelocity] = useState<{ month: string; count: number }[]>([]);
  const [meetingOutcomes, setMeetingOutcomes] = useState<{ name: string; count: number }[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);

  // Red/Green calendar capped at a 60-day span per the client's explicit request — a strictly
  // tighter selectable window than the 90-day backend sync lookback (HUBSPOT_LOOKBACK_DAYS /
  // MAX_RANGE_DAYS in server.py), so every pickable range still has real synced data behind it.
  const today = useMemo(() => new Date(), []);
  const lookbackStart = useMemo(() => { const d = new Date(today); d.setDate(d.getDate() - 60); return d; }, [today]);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, lookbackStart, today, 60));

  useEffect(() => {
    // Hits our own MongoDB (already synced from HubSpot in the background) — changing the date
    // range or any filter re-aggregates existing data, it never re-calls the HubSpot API.
    setLoading(true);
    api.get('/hubspot/summary', { params: { start_date: toISO(range.from), end_date: toISO(range.to), stages: selectedStages.join(','), owners: selectedOwners.join(',') } })
      .then((res) => { setSummary(res.data); setSyncedAt(res.data.synced_at); })
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [range, selectedStages, selectedOwners]);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await api.post('/hubspot/sync');
      const res = await api.get('/hubspot/summary', { params: { start_date: toISO(range.from), end_date: toISO(range.to), stages: selectedStages.join(','), owners: selectedOwners.join(',') } });
      setSummary(res.data);
      setSyncedAt(res.data.synced_at);
    } catch (e) {
      // connection badge staying stale surfaces the failure
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    api.get('/hubspot/companies/records', { params: { domain: selectedDomains.join(','), page: 1, limit: 15 } })
      .then((res) => setCompanies(res.data.items || []))
      .catch(() => {});
  }, [selectedDomains]);

  useEffect(() => {
    Promise.allSettled([api.get('/hubspot/contacts/velocity'), api.get('/hubspot/meetings/outcomes')]).then(([v, m]) => {
      if (v.status === 'fulfilled') setContactVelocity(v.value.data.velocity || []);
      if (m.status === 'fulfilled') setMeetingOutcomes(m.value.data.outcomes || []);
    });
  }, []);

  const activeFilterCount = selectedStages.length + selectedOwners.length + selectedDomains.length;

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="hubspot-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">Executive CRM Intelligence</span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">HubSpot CRM</h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">Live pipeline, accounts, and activity data synced directly via HubSpot's CRM API.</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={lookbackStart} maxDate={today} maxSpanDays={60} testIdPrefix="hubspot" />
          <button
            data-testid="hubspot-manual-sync-button"
            onClick={handleManualSync}
            disabled={syncing}
            className="px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs font-semibold border bg-white text-blue-700 border-blue-100 hover:border-blue-300 transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing…' : 'Manual Sync'}</span>
          </button>
          <div data-testid="hubspot-connection-badge" className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${summary?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
            <Building2 className="h-4.5 w-4.5" />
            <span>{summary?.connected ? `Synced ${syncedAt ? formatIST(syncedAt) : ''}` : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      {loading && !summary ? (
        <div data-testid="hubspot-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Loading live HubSpot CRM data…</div>
      ) : !summary?.connected ? (
        <div data-testid="hubspot-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">HubSpot isn't connected yet.</div>
      ) : (
        <>
          {/* Filter Bar */}
          <div className="bg-white border border-[#e8dcc9] rounded-2xl p-4 flex flex-wrap items-center gap-2 shadow-sm" data-testid="hubspot-filter-bar">
            <span className="text-[10px] font-mono uppercase text-slate-400 font-bold mr-1">Deep-Dive Filters:</span>
            <MultiSelect label="Deal Stage" options={summary.all_stages} selected={selectedStages} onChange={setSelectedStages} />
            <MultiSelect label="Deal Owner" options={summary.all_owners} selected={selectedOwners} onChange={setSelectedOwners} />
            {summary.all_domains.length > 0 && (
              <MultiSelect label="Company Domain" options={summary.all_domains} selected={selectedDomains} onChange={setSelectedDomains} />
            )}
            {activeFilterCount > 0 && (
              <button data-testid="hubspot-clear-filters" onClick={() => { setSelectedStages([]); setSelectedOwners([]); setSelectedDomains([]); }} className="flex items-center gap-1 text-[11px] text-rose-500 font-medium hover:underline ml-1">
                <X className="h-3 w-3" /> Clear ({activeFilterCount})
              </button>
            )}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard platform="HubSpot" title="Open Pipeline Value" value={inr(summary.total_open_value)} change={`${summary.open_deals_count} open deals`} trend="up"
              insight={{ title: 'Open Pipeline', metric: 'Open Pipeline Value', value: inr(summary.total_open_value), aiInsight: `${inr(summary.total_open_value)} across ${summary.open_deals_count} open deals matching current filters.`, prediction: 'Trend requires more history to project.', nextSteps: ['Review the Pipeline Stage Breakdown below for where deals are stuck.'] }}
              onHover={onHover} />
            <MetricCard platform="HubSpot" title="Win Rate" value={`${summary.win_rate.toFixed(1)}%`} change={`${summary.won_deals_count} won / ${summary.lost_deals_count} lost`} trend={summary.win_rate >= 50 ? 'up' : 'down'}
              insight={{ title: 'Win Rate', metric: 'Win Rate', value: `${summary.win_rate.toFixed(1)}%`, aiInsight: `${summary.won_deals_count} deals won vs ${summary.lost_deals_count} lost among closed deals matching current filters.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare win rate by owner in the chart below.'] }}
              onHover={onHover} />
            <MetricCard platform="HubSpot" title="Total Contacts" value={summary.total_contacts.toLocaleString('en-IN')} change={`${summary.total_companies} companies`} trend="up"
              insight={{ title: 'Contact Base', metric: 'Total Contacts', value: String(summary.total_contacts), aiInsight: `${summary.total_contacts} contacts across ${summary.total_companies} companies in your CRM.`, prediction: 'Trend requires more history to project.', nextSteps: ['Review the Contact Velocity chart below.'] }}
              onHover={onHover} />
            <MetricCard platform="HubSpot" title="Meetings / Won Deal" value={summary.activity_to_deal_index ? summary.activity_to_deal_index.toFixed(1) : '—'} change="Activity-to-Deal Index" trend="neutral"
              insight={{ title: 'Activity-to-Deal Conversion', metric: 'Meetings per Won Deal', value: summary.activity_to_deal_index ? summary.activity_to_deal_index.toFixed(1) : 'N/A', aiInsight: summary.activity_to_deal_index ? `On average, ${summary.activity_to_deal_index.toFixed(1)} meetings were logged per Closed-Won deal that had at least one meeting.` : 'Not enough meeting-to-deal association data yet.', prediction: 'Trend requires more history to project.', nextSteps: ['Review Appointment Outcomes below.'] }}
              onHover={onHover} />
          </div>

          {/* Pipeline Stage Breakdown + Value by Owner */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="hubspot-pipeline-funnel">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Pipeline Stage Breakdown</h3>
                <div className="flex items-center gap-3 text-[10px] font-mono text-slate-400">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#1d4ed8] inline-block" />Open</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />Won</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block" />Lost</span>
                </div>
              </div>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.stage_breakdown} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} width={130} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any) => [`${v} deals`, 'Count']} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Deals">
                      <LabelList dataKey="value" position="right" fill="#334155" fontSize={11} fontWeight="600" />
                      {summary.stage_breakdown.map((s, i) => <Cell key={i} fill={s.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="hubspot-value-by-owner">
              <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Deal Value by Owner</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.value_by_owner} layout="vertical" margin={{ top: 10, right: 60, left: 10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={(v) => inr(v)} />
                    <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} width={90} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} formatter={(v: any, n: any, p: any) => [`${inr(v)} across ${p.payload.deals} deals`, 'Deal Value']} />
                    <Bar dataKey="value" fill={BLUE} radius={[0, 4, 4, 0]} name="Deal Value">
                      <LabelList dataKey="value" position="right" fill="#334155" fontSize={10} formatter={(v: any) => inr(v)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Accounts Ledger + Contact Velocity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="hubspot-accounts-ledger">
              <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Executive Accounts Ledger</h3>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-4">Company</th>
                      <th className="py-2 pr-4">Tier</th>
                      <th className="py-2 pr-4 text-right">Employees</th>
                      <th className="py-2">Sample Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((c) => (
                      <tr key={c.id} className="border-b border-slate-50 last:border-0">
                        <td className="py-2.5 pr-4 text-slate-700 max-w-[140px] truncate">{c.name}</td>
                        <td className="py-2.5 pr-4"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">{c.tier}</span></td>
                        <td className="py-2.5 pr-4 text-right text-slate-500">{c.numberofemployees || '—'}</td>
                        <td className="py-2.5 text-slate-500 max-w-[160px] truncate">{c.sample_contact || `${c.contact_count} contacts`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="hubspot-contact-velocity">
              <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Contact Velocity (Monthly)</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={contactVelocity} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                    <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} />
                    <Line type="monotone" dataKey="count" stroke={BLUE} strokeWidth={2.5} dot={{ r: 3 }} name="New Contacts" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Appointment Outcomes */}
          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="hubspot-meeting-outcomes">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Appointment Velocity & Outcomes</h3>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={meetingOutcomes} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(29,78,216,0.06)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#dbeafe', borderRadius: '12px' }} />
                  <Bar dataKey="count" fill={SKY} radius={[4, 4, 0, 0]} name="Meetings" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
